import { X } from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";

interface ModalProps {
	isOpen: boolean;
	onClose?: () => void;
	title?: string;
	children: React.ReactNode;
}

const trapFocus = (contentEl: HTMLDivElement | null, e: KeyboardEvent) => {
	if (e.key !== "Tab" || !contentEl) return;

	const focusableElements = contentEl.querySelectorAll<HTMLElement>(
		'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
	);

	const len = focusableElements.length;
	if (len === 0) {
		e.preventDefault();
		return;
	}

	const first = focusableElements[0];
	const last = focusableElements[len - 1];
	if (!first || !last) return;

	const active = document.activeElement;
	if (e.shiftKey && active === first) {
		last.focus();
		e.preventDefault();
	} else if (!e.shiftKey && active === last) {
		first.focus();
		e.preventDefault();
	}
};

export const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	children,
}) => {
	const overlayRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const mouseDownOnOverlay = useRef(false);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && onClose) onClose();
		};

		const handleTab = (e: KeyboardEvent) => {
			trapFocus(contentRef.current, e);
		};

		const previousActiveElement = document.activeElement as HTMLElement | null;
		let focusTimer: ReturnType<typeof setTimeout> | null = null;

		if (isOpen) {
			document.body.style.overflow = "hidden";
			if (onClose) {
				window.addEventListener("keydown", handleEscape);
			}
			window.addEventListener("keydown", handleTab);

			if (contentRef.current) {
				const focusableElements =
					contentRef.current.querySelectorAll<HTMLElement>(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					);
				if (focusableElements.length > 0) {
					focusTimer = setTimeout(() => {
						const firstElem = focusableElements[0];
						if (firstElem) firstElem.focus();
					}, 50);
				} else {
					contentRef.current.focus();
				}
			}
		}

		return () => {
			document.body.style.overflow = "unset";
			window.removeEventListener("keydown", handleEscape);
			window.removeEventListener("keydown", handleTab);
			if (focusTimer) {
				clearTimeout(focusTimer);
			}
			if (
				previousActiveElement &&
				typeof previousActiveElement.focus === "function"
			) {
				previousActiveElement.focus();
			}
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const handleMouseDown = (e: React.MouseEvent) => {
		if (e.target === overlayRef.current) {
			mouseDownOnOverlay.current = true;
		} else {
			mouseDownOnOverlay.current = false;
		}
	};

	const handleMouseUp = (e: React.MouseEvent) => {
		if (
			e.target === overlayRef.current &&
			mouseDownOnOverlay.current &&
			onClose
		) {
			onClose();
		}
		mouseDownOnOverlay.current = false;
	};

	if (typeof window === "undefined" || !document.body) {
		return null;
	}

	return createPortal(
		// biome-ignore lint/a11y/noStaticElementInteractions: Overlay needs mouse events for backdrop click handling
		<div
			className="modal-overlay"
			ref={overlayRef}
			onMouseDown={handleMouseDown}
			onMouseUp={handleMouseUp}
		>
			<div
				className="modal-content"
				ref={contentRef}
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				tabIndex={-1}
			>
				<div className="modal-header">
					{title && <h2 className="modal-title">{title}</h2>}
					{onClose && (
						<button
							type="button"
							className="modal-close-button"
							onClick={onClose}
							title="Close"
						>
							<X size={20} />
						</button>
					)}
				</div>
				<div className="modal-body">{children}</div>
			</div>
		</div>,
		document.body,
	);
};
